import React from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  SITE,
  CANONICAL,
  TITLE,
  DESC,
  INTRO_PARAS,
  TYPE_GUIDE,
  TOW_STEPS,
  FAQ,
} from './quizContent';
import QuizWizard from './QuizWizard';

import css from './QuizPage.module.css';

// ------------------------------------------------------------------
// BigLoads "What Trailer Do I Need?" — AI trailer finder + SEO page.
//
//   Canonical URL: /what-trailer-do-i-need
//
//   The calculator-page SEO formula: an interactive AI tool (the quiz)
//   as the hook, wrapped in substantial crawlable content (type guides,
//   tow guidance, FAQ) + FAQPage/HowTo/WebApplication schema. The tool
//   converts; the content ranks for informational renter searches like
//   "what trailer do I need" / "what size trailer to haul X".
//
//   The AI recommendation runs SERVER-SIDE (server/api/quiz/recommend.js
//   -> Grok), so the page works fully autonomously with no dependency on
//   any local machine.
// ------------------------------------------------------------------

const QuizPageComponent = ({ scrollingDisabled }) => {
  const webAppSchema = {
    '@type': 'WebApplication',
    name: 'BigLoads Trailer Finder',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: CANONICAL,
    description: DESC,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
  const howToSchema = {
    '@type': 'HowTo',
    name: 'How to know if your vehicle can tow a trailer',
    step: TOW_STEPS.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'What Trailer Do I Need', item: CANONICAL },
    ],
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={TITLE}
      description={DESC}
      schema={[webAppSchema, howToSchema, breadcrumbSchema, faqSchema]}
      canonicalUrl={CANONICAL}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        {/* ===== HERO + QUIZ (the hook) ===== */}
        <section
          className={css.hero}
          id="trailer-finder"
          style={{
            backgroundImage:
              'linear-gradient(100deg, rgba(15,15,18,0.9) 0%, rgba(15,15,18,0.72) 42%, rgba(15,15,18,0.4) 70%, rgba(15,15,18,0.25) 100%), url(/static/seo/quiz-hero-trailers.jpg)',
          }}
        >
          <div className={css.heroInner}>
            <div className={css.heroCopy}>
              <span className={css.eyebrow}>Free AI trailer finder</span>
              <h1 className={css.h1}>What trailer do I need?</h1>
              <p className={css.sub}>
                Answer 4 quick questions. Our AI tells you the exact trailer type and size, whether
                your vehicle can safely tow it, and shows real trailers for rent near you.
              </p>
            </div>
            <QuizWizard />
          </div>
        </section>

        {/* ===== SEO CONTENT WRAPPER (what ranks) ===== */}
        <div className={css.content}>
          <section className={css.section}>
            {INTRO_PARAS.map((p, i) => (
              <p key={i} className={css.para}>
                {p}
              </p>
            ))}
          </section>

          <section className={css.section}>
            <h2 className={css.h2}>The main trailer types, and what each is for</h2>
            <p className={css.para}>
              Here is a plain-English rundown of the most common trailers people rent. If you are
              still not sure after reading, run the finder above and it will pick for you.
            </p>
            <div className={css.typeGrid}>
              {TYPE_GUIDE.map(t => (
                <div key={t.key} className={css.typeCard}>
                  <h3 className={css.typeName}>{t.name}</h3>
                  <p className={css.typeBest}>
                    <strong>Best for:</strong> {t.best}
                  </p>
                  <p className={css.typeDetail}>{t.detail}</p>
                  <a className={css.typeLink} href={t.hub}>
                    See {t.name.toLowerCase()}s for rent near you
                  </a>
                </div>
              ))}
            </div>
          </section>

          <section className={css.section}>
            <h2 className={css.h2}>Can your truck or SUV tow it?</h2>
            <p className={css.para}>
              The right trailer type is only half the answer. The other half is whether your vehicle
              can safely tow it loaded. The finder above gives you an honest read on your specific
              vehicle, but here is how to check it yourself.
            </p>
            <ol className={css.towList}>
              {TOW_STEPS.map((s, i) => (
                <li key={i} className={css.towItem}>
                  <span className={css.towNum}>{i + 1}</span>
                  <div>
                    <strong className={css.towName}>{s.name}</strong>
                    <p className={css.towText}>{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className={css.section}>
            <h2 className={css.h2}>Frequently asked questions</h2>
            <div className={css.faqList}>
              {FAQ.map((f, i) => (
                <div key={i} className={css.faqItem}>
                  <h3 className={css.faqQ}>{f.q}</h3>
                  <p className={css.faqA}>{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={css.ctaBand}>
            <h2 className={css.ctaTitle}>Ready to find your trailer?</h2>
            <p className={css.ctaSub}>
              Run the free finder, or browse trailers for rent from local owners near you.
            </p>
            <div className={css.ctaBtns}>
              <a href="#trailer-finder" className={css.ctaPrimary}>
                Find my trailer
              </a>
              <a href="/s" className={css.ctaSecondary}>
                Browse all trailers
              </a>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

QuizPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => ({ scrollingDisabled: isScrollingDisabled(state) });

const QuizPage = compose(connect(mapStateToProps))(QuizPageComponent);

export default QuizPage;
